import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, Send, Settings2, ChevronDown, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, fmtDate, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';
import { usePayEngine } from '../Settings/payRatesKit';
import XeroRatesPayroll from './XeroRatesPayroll';

// Timesheets › Xero payroll: send one pay period's hours to Xero as draft timesheets, split by pay
// band onto each worker's own Xero pay items. Xero keeps the pay rates (SCHADS penalties as pay
// item multiples or the worker's own rates); Rostery sends hours. Backend: routes/xeroSync.js.

const BANDS = [
  ['weekday', 'Weekday', 'Mon–Fri 6am–8pm'],
  ['evening', 'Evening', 'Mon–Fri 8pm–midnight'],
  ['night', 'Night', 'Mon–Fri midnight–6am'],
  ['saturday', 'Saturday', ''],
  ['sunday', 'Sunday', ''],
  ['public_holiday', 'Public holiday', ''],
  ['sleepover', 'Sleepover', 'one per sleepover shift'],
];
const STATUS = {
  ready: { tone: 'success', label: 'Ready' },
  in_xero: { tone: 'info', label: 'Already in Xero' },
  needs_mapping: { tone: 'warning', label: 'Pay item missing' },
  not_in_xero: { tone: 'neutral', label: 'Not in Xero payroll' },
  other_calendar: { tone: 'neutral', label: 'Other pay calendar' },
  contractor: { tone: 'neutral', label: 'Contractor' },
  no_hours: { tone: 'neutral', label: 'No hours' },
};
const h = (v) => (v ? Number(v).toFixed(2).replace(/\.00$/, '') : '');

function XeroTimesheetsView() {
  const confirm = useConfirm();
  // Payroll: Send to Xero sends; Integrations: Xero matches pay items (Pay Rates v1.2 section 10).
  const { can } = usePayPermissions();
  const canSend = can('payroll.send_xero');
  const canSetup = can('integrations.xero');
  const [setup, setSetup] = useState(null);
  const [setupError, setSetupError] = useState(null);
  const [calendarId, setCalendarId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const [form, setForm] = useState(null);

  const loadSetup = useCallback(async () => {
    setSetupError(null);
    try {
      // Ask whether Xero is connected first, as the invoices tab does: the set-up call on an org
      // without Xero answers 400, which every visit logged as an error in the console.
      const { data: st } = await api.get('/xero-sync/status').catch(() => ({ data: { connected: false } }));
      if (!st?.connected) { setSetupError({ code: 'XERO_NOT_CONNECTED' }); return; }
      const { data } = await api.get('/xero-sync/payroll/setup');
      setSetup(data);
      setForm(data.settings);
      // Calendars arrive busiest first: open on the one most workers are paid on.
      const usable = data.calendars.filter((c) => c.supported);
      const cal = usable[0];
      if (cal) {
        setCalendarId((cur) => cur || cal.id);
        const p = cal.periods.find((x) => x.ended) || cal.periods[0];
        setPeriodStart((cur) => cur || (p && p.start) || '');
      }
      if (!data.saved) setMapOpen(true);
    } catch (e) { setSetupError(e.response?.data || { message: 'Could not read your Xero payroll set-up.' }); }
  }, []);
  useEffect(() => { loadSetup(); }, [loadSetup]);

  const calendar = useMemo(() => (setup?.calendars || []).find((c) => c.id === calendarId), [setup, calendarId]);

  const load = useCallback(async () => {
    if (!calendarId || !periodStart) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/xero-sync/payroll/preview?calendarId=${calendarId}&periodStart=${periodStart}`);
      setPreview(data);
      // A period Xero has already paid starts with nobody ticked.
      setPicked(data.processedInXero ? new Set() : new Set(data.rows.filter((r) => r.status === 'ready').map((r) => r.userId)));
    } catch (e) { setError(e.response?.data?.message || 'Could not prepare the timesheets.'); }
    finally { setLoading(false); }
  }, [calendarId, periodStart]);
  useEffect(() => { if (setup && !setup.needsReconnect) load(); }, [setup, load]);

  const saveMap = async () => {
    setBusy('map');
    try {
      await api.put('/xero-sync/settings', { payroll: form });
      toast.success('Pay item matching saved');
      setMapOpen(false);
      await loadSetup(); await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setBusy(''); }
  };

  const rows = preview?.rows || [];
  const ready = rows.filter((r) => r.status === 'ready');
  const chosen = ready.filter((r) => picked.has(r.userId));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const send = async () => {
    const ok = await confirm(
      preview.processedInXero
        ? `Xero has ALREADY PAID this period (${plural(preview.processedInXero, 'timesheet')} processed). Only send for a worker who was left out of that pay run, or they will be paid twice.`
        : `${plural(chosen.length, 'draft timesheet')} for ${fmtDate(preview.periodStart)} to ${fmtDate(preview.periodEnd)} go to Xero. Check them, add anything Rostery doesn't send, then run the pay run in Xero.`,
      { title: `Send ${plural(chosen.length, 'timesheet')} to Xero payroll?`, confirmLabel: preview.processedInXero ? 'Send anyway' : 'Send to Xero', danger: !!preview.processedInXero });
    if (!ok) return;
    setBusy('send');
    try {
      const { data } = await api.post('/xero-sync/payroll/push', { calendarId, periodStart, userIds: chosen.map((r) => r.userId), confirmPaidPeriod: !!preview.processedInXero });
      const good = data.results.filter((r) => r.ok);
      const bad = data.results.filter((r) => !r.ok);
      if (good.length) toast.success(`${plural(good.length, 'timesheet')} in Xero as drafts`);
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sending to Xero failed'); }
    finally { setBusy(''); }
  };

  if (setupError) {
    const notConnected = setupError.code === 'XERO_NOT_CONNECTED';
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title={notConnected ? 'Xero is not connected' : 'Xero needs one more permission'}
          hint={notConnected ? 'Connect Xero to send timesheets to it.' : setupError.message}
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }
  if (!setup) return <div className="card"><TableSkeleton rows={4} cols={6} /></div>;
  if (setup.needsReconnect) {
    return (
      <Notice tone="amber" title="Reconnect Xero to send timesheets"
        actions={<Link to="/settings?tab=integrations" className="btn-secondary btn-sm">Go to integrations</Link>}>
        Rostery needs permission to read your Xero pay calendars and pay items. In Settings › Integrations, press Reconnect on Xero and allow access, then come back here.
      </Notice>
    );
  }

  // Only pay items paid per unit can take hours; fixed-amount ones (allowances, fees) can't.
  const rates = (setup.earningsRates || []).filter((r) => r.rateType === 'RATEPERUNIT' || r.rateType === 'MULTIPLE');
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0 font-bold text-[#13B5EA]" aria-hidden="true">X</span>
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-base font-semibold text-slate-900">Send timesheets to Xero payroll</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-[72ch]">
              Approved hours for one pay period, split into weekday, evening, night, Saturday, Sunday and public holiday,
              each on the worker's matching Xero pay item. Xero keeps the pay rates. Timesheets go as drafts, once per worker per period.
            </p>
          </div>
          {canSetup && (
            <button type="button" className="btn-secondary btn-sm" aria-expanded={mapOpen} onClick={() => setMapOpen((v) => !v)}>
              <Settings2 size={13} aria-hidden="true" /> Pay item matching
            </button>
          )}
        </div>

        {!setup.saved && !canSetup && (
          <p className="text-[13px] text-amber-700">Pay items have not been matched yet. Someone with the Integrations: Xero permission needs to check them first.</p>
        )}

        {mapOpen && form && canSetup && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            {!setup.saved && <p className="text-[13px] text-amber-700">First guess from your Xero pay item names. Check each one, then save.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
              {BANDS.map(([k, name, hint]) => (
                <div key={k} className="grid grid-cols-[minmax(0,10rem)_1fr] items-center gap-2">
                  <label htmlFor={`pb-${k}`} className="text-[13px] text-slate-700">{name}{hint && <span className="block text-[11px] text-slate-400">{hint}</span>}</label>
                  <select id={`pb-${k}`} className="input-field" value={form.bandMap?.[k] || ''}
                    onChange={(e) => setForm((f) => ({ ...f, bandMap: { ...f.bandMap, [k]: e.target.value || null } }))}>
                    {k === 'weekday' ? <option value="ORDINARY">Each worker's ordinary rate</option>
                      : <option value="">{['evening', 'night'].includes(k) ? 'Not matched: paid as ordinary' : 'Not matched'}</option>}
                    {rates.map((r) => <option key={r.id} value={r.id}>{r.name}{r.rateType === 'MULTIPLE' && r.multiplier ? ` (×${r.multiplier})` : ''}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700 pt-1">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={form.source === 'timesheets_and_roster'} onChange={(e) => setForm((f) => ({ ...f, source: e.target.checked ? 'timesheets_and_roster' : 'timesheets' }))} />
                Also send rostered shifts that have no approved timesheet
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={!!form.includeContractors} onChange={(e) => setForm((f) => ({ ...f, includeContractors: e.target.checked }))} />
                Include contractors
              </label>
            </div>
            <p className="text-[12px] text-slate-500">
              Tip: set Xero's Saturday, Sunday, public holiday, evening and night pay items as multiples of the ordinary rate (SCHADS: 1.5, 2, 2.5, plus loadings), so Xero works out the pay.
              Saturday, Sunday and public holiday hours are never paid at the ordinary rate by mistake: a worker with those hours waits until they're matched.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary btn-sm" onClick={() => { setForm(setup.settings); setMapOpen(false); }}>Cancel</button>
              <button type="button" className="btn-primary btn-sm" disabled={busy === 'map'} onClick={saveMap}>
                {busy === 'map' && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Save matching
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="xp-cal" className="text-[13px] text-slate-700 font-medium block mb-1">Pay calendar</label>
            <select id="xp-cal" className="input-field" value={calendarId} onChange={(e) => { setCalendarId(e.target.value); setPeriodStart(''); setPreview(null); }}>
              {setup.calendars.map((c) => <option key={c.id} value={c.id} disabled={!c.supported}>{c.name.trim()} ({c.type.toLowerCase()}{c.supported ? `, ${plural(c.employees || 0, 'worker')}` : ', not supported'})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="xp-period" className="text-[13px] text-slate-700 font-medium block mb-1">Pay period</label>
            <select id="xp-period" className="input-field" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}>
              <option value="">Choose…</option>
              {(calendar?.periods || []).map((p) => (
                <option key={p.start} value={p.start}>{fmtDate(p.start)} – {fmtDate(p.end)}{p.current ? ' (current)' : ''}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading || !periodStart}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Show hours
          </button>
        </div>
      </div>

      {preview?.processedInXero > 0 && (
        <Notice tone="rose" role="alert" title="This pay period has already been paid in Xero">
          Xero has {plural(preview.processedInXero, 'processed timesheet')} for {fmtDate(preview.periodStart)} – {fmtDate(preview.periodEnd)}. Nobody is ticked.
          Only send for a worker who was left out of that pay run; anyone else would be paid twice.
        </Notice>
      )}

      <div className="card overflow-hidden">
        {loading && !preview ? <TableSkeleton rows={5} cols={8} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !preview ? <EmptyState icon={Send} title="Choose a pay period" hint="Then Rostery shows each worker's hours for it." />
          : !rows.length ? <EmptyState icon={Send} title="No approved hours in this period" hint="Approve timesheets first, or tick “Also send rostered shifts” in Pay item matching." />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10"><span className="sr-only">Send</span></th>
                      <th className="px-3 py-2.5 font-medium">Worker</th>
                      {BANDS.map(([k, name]) => <th key={k} className="px-2 py-2.5 font-medium text-right whitespace-nowrap">{name === 'Public holiday' ? 'Pub. hol.' : name}</th>)}
                      <th className="px-3 py-2.5 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => {
                      const st = STATUS[r.status] || STATUS.no_hours;
                      return (
                        <tr key={r.userId} className="align-top">
                          <td className="px-4 py-3">
                            <input type="checkbox" aria-label={`Send ${r.name}`} disabled={r.status !== 'ready'} checked={picked.has(r.userId)} onChange={() => toggle(r.userId)} />
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-slate-900">{r.name}</p>
                            <p className="text-[12px] text-slate-500">{plural(r.shifts, 'shift')}{r.xeroName && r.xeroName !== r.name ? ` · Xero: ${r.xeroName}` : ''}</p>
                            {r.warnings.map((w) => <p key={w} className="text-[12px] text-amber-700 mt-1">{w}</p>)}
                          </td>
                          {BANDS.map(([k]) => <td key={k} className="px-2 py-3 text-right tabular-nums text-slate-700">{h(r.totals[k])}</td>)}
                          <td className="px-3 py-3">
                            <StatusPill tone={st.tone} label={st.label} />
                            {r.reason && r.status !== 'ready' && <p className="text-[12px] text-slate-500 mt-1 max-w-[16rem]">{r.reason}</p>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">
                  {fmtDate(preview.periodStart)} – {fmtDate(preview.periodEnd)} · {plural(ready.length, 'worker')} ready{rows.length > ready.length ? `, ${rows.length - ready.length} not sent` : ''}.
                </p>
                <button type="button" className="btn-primary" disabled={!chosen.length || !!busy || !canSend} onClick={send}
                  title={canSend ? undefined : needsPermission('payroll.send_xero')}>
                  {busy === 'send' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
                  Send {plural(chosen.length, 'timesheet')} to Xero
                </button>
              </div>
            </>
          )}
      </div>
      {preview && (
        <details className="card p-4 text-[13px] text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-800 inline-flex items-center gap-1">How hours are split <ChevronDown size={13} aria-hidden="true" /></summary>
          <p className="mt-2 max-w-[80ch]">
            Approved timesheet hours (after breaks) are spread across the bands the worker's clock times fall in. A shift past midnight changes day at midnight.
            Public holidays come from Rostery's public holiday list. Sleepover shifts send one unit each instead of hours. Allowances, kilometres and expenses are not sent.
          </p>
        </details>
      )}
    </div>
  );
}

// Timesheets › Xero payroll: the timesheet send, and the staff who are (or are not yet) in Xero.
export default function XeroPayrollTab() {
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get('view') === 'staff' ? 'staff' : 'timesheets'));
  const { can } = usePayPermissions();
  // A company paid from Pay Rates sends pay lines (8 mapped categories, pay template rate sync, back
  // pay); every other company keeps the pay-band send below, unchanged.
  const engine = usePayEngine();
  if (!can('payroll.send_xero') && !can('integrations.xero')) {
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title="Xero payroll is not part of your role"
          hint="Sending timesheets to Xero needs the Payroll: Send to Xero permission, and setting up Xero payroll needs Integrations: Xero. Ask your company admin to add them to your role." />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="Xero payroll">
        {[['timesheets', 'Timesheets'], ['staff', 'Staff']].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${view === id ? 'bg-[#13B5EA] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {view !== 'timesheets' ? <XeroStaffView /> : engine === 'rates' ? <XeroRatesPayroll /> : <XeroTimesheetsView />}
    </div>
  );
}

const XERO_STAFF_STATUS = {
  ready: { tone: 'success', label: 'Ready to add' },
  in_xero: { tone: 'info', label: 'In Xero' },
  missing_dob: { tone: 'warning', label: 'No date of birth' },
  missing_address: { tone: 'warning', label: 'No home address' },
  missing_name: { tone: 'warning', label: 'Name incomplete' },
  contractor: { tone: 'neutral', label: 'Contractor' },
};

// Rostery staff and whether each is in Xero Payroll; add the ones who are not.
function XeroStaffView() {
  const confirm = useConfirm();
  // Adding and linking employees in Xero is Integrations: Xero (Pay Rates v1.2 section 10).
  const { can } = usePayPermissions();
  const canSetup = can('integrations.xero');
  const setupTitle = canSetup ? undefined : needsPermission('integrations.xero');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [calendarId, setCalendarId] = useState('');
  const [rateId, setRateId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: st } = await api.get('/xero-sync/status').catch(() => ({ data: { connected: false } }));
      if (!st?.connected) { setError('Xero is not connected. Connect it in Settings › Integrations.'); return; }
      const { data: d } = await api.get('/xero-sync/payroll/staff');
      setData(d);
      setCalendarId((v) => v || d.calendars[0]?.id || '');
      setRateId((v) => v || d.ordinaryRates[0]?.id || '');
      setPicked(new Set());
    } catch (e) { setError(e.response?.data?.message || 'Could not read your Xero payroll staff.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const staff = data?.staff || [];
  const ready = staff.filter((s) => s.status === 'ready');
  const chosen = ready.filter((s) => picked.has(s.userId));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(ready.map((s) => s.userId)));

  const add = async (list = chosen) => {
    if (!list.length) return;
    const ok = await confirm(
      `${plural(list.length, 'employee')} will be created in Xero Payroll with their name, date of birth, home address, contact details, start date, the chosen pay calendar and their Rostery hourly rate. Then finish their tax declaration, bank and super in Xero.`,
      { title: `Add ${plural(list.length, 'staff member')} to Xero Payroll?`, confirmLabel: 'Add to Xero' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/xero-sync/payroll/staff/add', { userIds: list.map((s) => s.userId), calendarId, ordinaryRateId: rateId });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'staff member')} added to Xero Payroll`);
      // Someone already in Xero is never created twice: say so plainly.
      if (already.length) toast.error(`Already in Xero, not created again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Adding staff to Xero failed'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={5} cols={4} /></div>;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Staff in Xero Payroll</h3>
          <button type="button" className="btn-primary" disabled={!ready.length || busy || !calendarId || !rateId || !canSetup} onClick={() => add(ready)} title={setupTitle}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
            {ready.length ? `Add all ${plural(ready.length, 'ready staff member')} to Xero` : 'Everyone ready is in Xero'}
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-[72ch]">
          Timesheets go only to workers who are in Xero Payroll. Add the others here: Rostery creates them with their personal details, start date,
          pay calendar and an ordinary pay line at their Rostery hourly rate. Someone already in Xero is linked, never added twice.
        </p>
        <div className="flex flex-wrap items-end gap-3 pt-2">
          <div>
            <label htmlFor="xs-cal" className="text-[13px] text-slate-700 font-medium block mb-1">Pay calendar</label>
            <select id="xs-cal" className="input-field" value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
              {data.calendars.map((c) => <option key={c.id} value={c.id}>{c.name} ({String(c.type || '').toLowerCase()})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="xs-rate" className="text-[13px] text-slate-700 font-medium block mb-1">Ordinary hours pay item</label>
            <select id="xs-rate" className="input-field" value={rateId} onChange={(e) => setRateId(e.target.value)}>
              {data.ordinaryRates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden">
        {!staff.length ? <EmptyState icon={UserPlus} title="No active staff" hint="Staff appear here once they are in Rostery." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every staff member ready to add" disabled={!ready.length} checked={allPicked} onChange={toggleAll} /></th>
                    <th className="px-3 py-2.5 font-medium">Staff member</th>
                    <th className="px-3 py-2.5 font-medium">Pay group</th>
                    <th className="px-2 py-2.5 font-medium text-right">Hourly rate</th>
                    <th className="px-3 py-2.5 font-medium">Xero</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((s) => {
                    const st = XERO_STAFF_STATUS[s.status] || XERO_STAFF_STATUS.in_xero;
                    return (
                      <tr key={s.userId} className="align-top">
                        <td className="px-4 py-3"><input type="checkbox" aria-label={`Add ${s.name}`} disabled={s.status !== 'ready'} checked={picked.has(s.userId)} onChange={() => toggle(s.userId)} /></td>
                        <td className="px-3 py-3"><p className="font-medium text-slate-900">{s.name}</p><p className="text-[12px] text-slate-500 break-all">{s.email}</p></td>
                        <td className="px-3 py-3 text-slate-700">{s.payGroup || <span className="text-slate-400">—</span>}</td>
                        <td className="px-2 py-3 text-right tabular-nums text-slate-700">{s.hourlyRate ? `$${s.hourlyRate.toFixed(2)}` : <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-3">
                          <StatusPill tone={st.tone} label={st.label} />
                          {s.xeroName && s.xeroName !== s.name && <p className="text-[12px] text-slate-500 mt-1">As {s.xeroName}</p>}
                          {s.reason && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{s.reason}</p>}
                          {['missing_dob', 'missing_name', 'missing_address'].includes(s.status) && <Link to={`/staff/${s.userId}/edit`} className="text-[12px] text-sky-700 font-medium hover:underline">Fix in their profile</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-600 flex-1">{plural(staff.filter((s) => s.status === 'in_xero').length, 'staff member')} in Xero · {ready.length} ready to add.</p>
              <button type="button" className="btn-primary" disabled={!chosen.length || busy || !calendarId || !rateId || !canSetup} onClick={() => add(chosen)} title={setupTitle}>
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
                Add {plural(chosen.length, 'staff member')} to Xero
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
